import { useId, useState } from 'react';

import { useCounterMode, useCan } from '@/shared/session/profiles';
import type { Supplier, SupplierCondition, SupplierConditionWrite } from '@/shared/tariffs/types';
import { useFamilies, usePutConditions } from '@/shared/tariffs/useTariffs';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PenIcon, PlusIcon, XIcon } from '@/shared/ui/icons';
import { TBody, THead, Table, Td, Th, Tr } from '@/shared/ui/Table';
import { decimalFor, editableNumber, money, percent } from '@/shared/utils/money';

import { usePriceSight } from './sight';

/** The purchase a row says, if it says one: the net price of the tariff, or a discount on
 * the list price. */
function purchaseOf(condition: SupplierCondition | undefined): string | null {
  if (!condition) return null;
  if (condition.purchase_net_in_tariff) return 'El neto de la tarifa';
  return condition.purchase_discount_pct
    ? `${percent(condition.purchase_discount_pct)} sobre PVP`
    : null;
}

/**
 * A cell of the table as the back applies it (`tariff_views.ConditionBook`): what the row
 * says, or, on a family that leaves it empty, the general one's, muted and saying so. A
 * family row of «—» read as «no discount» when its articles carry the general 25 %.
 */
function Inherited({
  own,
  general,
  isFamily,
}: {
  own: string | null;
  general: string | null;
  isFamily: boolean;
}) {
  if (own !== null) return <>{own}</>;
  if (isFamily && general !== null) {
    return <span className="text-ink-3">{general} (general)</span>;
  }
  return <span className="text-ink-4">—</span>;
}

/** `percent` and `money` with nothing for an empty value, so `Inherited` can fall back. */
function given(format: (value: string) => string, value: string | null | undefined) {
  return value === null || value === undefined || value === '' ? null : format(value);
}

/** General first, then the families as they are written. */
function sorted(conditions: readonly SupplierCondition[]): SupplierCondition[] {
  return [...conditions].sort((a, b) =>
    a.family === '' ? -1 : b.family === '' ? 1 : (a.family ?? '').localeCompare(b.family ?? ''),
  );
}

/**
 * The conditions of a supplier (contract v10.48): the general ones and, where they differ,
 * one row per family (Bosch: one discount for machines and another for accessories,
 * meeting of 01/10). A family row inherits from the general one what it leaves empty; the
 * back works out each article's from that, and the table says it.
 *
 * The notes are for every profile and stay in the counter mode (contract v10.51): what the
 * shop and the salesperson in the street have to know, «el proveedor solo sirve cajas
 * completas» (Víctor, meeting of 08/09). Until v10.50 they went with the maximum discount.
 */
export function ConditionsCard({ supplier }: { supplier: Supplier }) {
  const sight = usePriceSight();
  const manage = useCan('manage_documents');
  const counter = useCounterMode();
  const [editing, setEditing] = useState(false);
  const rows = sorted(supplier.conditions ?? []);
  const general = rows.find((row) => row.family === '');

  return (
    <Card
      title="Condiciones"
      description="Lo que se aplica a cada artículo de sus tarifas, por familia si hace falta."
      aside={
        manage ? (
          <Button
            variant="secondary"
            size="sm"
            icon={<PenIcon size={14} />}
            onClick={() => setEditing(true)}
            disabled={counter}
            title={counter ? 'Quita el modo mostrador para editarlas' : undefined}
          >
            Editar
          </Button>
        ) : undefined
      }
      padded={rows.length === 0}
    >
      {rows.length === 0 ? (
        <p className="text-ink-3 text-[14px]">Sin condiciones: se busca con el PVP de la tarifa.</p>
      ) : (
        <Table label="Condiciones por familia" minWidth="36rem">
          <THead>
            <tr>
              <Th>Familia</Th>
              <Th className="text-right">Dto. habitual</Th>
              {sight.maxDiscount ? <Th className="text-right">Dto. máximo</Th> : null}
              {sight.purchase ? <Th>Compra</Th> : null}
              <Th className="text-right">Porte gratis desde</Th>
              <Th>Notas</Th>
            </tr>
          </THead>
          <TBody>
            {rows.map((condition) => {
              const isFamily = Boolean(condition.family);
              return (
                <Tr key={condition.family ?? ''}>
                  <Td className="text-ink font-medium">{condition.family || 'General'}</Td>
                  <Td className="text-right tabular-nums">
                    <Inherited
                      own={given(percent, condition.usual_discount_pct)}
                      general={given(percent, general?.usual_discount_pct)}
                      isFamily={isFamily}
                    />
                  </Td>
                  {sight.maxDiscount ? (
                    <Td className="text-right tabular-nums">
                      <Inherited
                        own={given(percent, condition.max_discount_pct)}
                        general={given(percent, general?.max_discount_pct)}
                        isFamily={isFamily}
                      />
                    </Td>
                  ) : null}
                  {sight.purchase ? (
                    <Td>
                      {/* Nothing anywhere is the net price: the cost is the net column then. */}
                      <Inherited
                        own={purchaseOf(condition) ?? (isFamily ? null : 'El neto de la tarifa')}
                        general={purchaseOf(general) ?? 'El neto de la tarifa'}
                        isFamily={isFamily}
                      />
                    </Td>
                  ) : null}
                  <Td className="text-right tabular-nums">
                    <Inherited
                      own={given(money, condition.free_shipping_from)}
                      general={given(money, general?.free_shipping_from)}
                      isFamily={isFamily}
                    />
                  </Td>
                  <Td className="text-ink-2 text-[13.5px]">{condition.notes ?? ''}</Td>
                </Tr>
              );
            })}
          </TBody>
        </Table>
      )}
      {/* The counter mode switched on in another tab closes it: the form shows every field. */}
      {editing && !counter ? (
        <ConditionsDialog supplier={supplier} rows={rows} onDone={() => setEditing(false)} />
      ) : null}
    </Card>
  );
}

/**
 * The conditions in a few lines, beside a catalog (contract v10.49): the usual discount and
 * the free shipping of each family, the notes, and, for whoever may see it, the maximum.
 * The articles found already carry their own price with the discount; this is what
 * the salesperson says on top («desde 300 € no paga portes»).
 */
export function ConditionsSummary({ supplier }: { supplier: Supplier }) {
  const sight = usePriceSight();
  const rows = sorted(supplier.conditions ?? []);
  const general = rows.find((row) => row.family === '');
  if (rows.length === 0) {
    return <p className="text-ink-3 text-[13.5px]">Sin condiciones: se da el PVP de la tarifa.</p>;
  }
  return (
    <ul className="flex flex-col gap-1.5 text-[13.5px]">
      {rows.map((condition) => {
        const isFamily = Boolean(condition.family);
        const usual =
          given(percent, condition.usual_discount_pct) ??
          (isFamily ? given(percent, general?.usual_discount_pct) : null);
        const max = sight.maxDiscount
          ? (given(percent, condition.max_discount_pct) ??
            (isFamily ? given(percent, general?.max_discount_pct) : null))
          : null;
        const shipping =
          given(money, condition.free_shipping_from) ??
          (isFamily ? given(money, general?.free_shipping_from) : null);
        const parts = [
          usual ? `dto. ${usual}` : 'sin dto.',
          ...(max ? [`máx. ${max}`] : []),
          ...(shipping ? [`porte gratis desde ${shipping}`] : []),
        ];
        return (
          <li key={condition.family ?? ''}>
            <span className="text-ink font-medium">{condition.family || 'General'}</span>
            <span className="text-ink-2">: {parts.join(' · ')}</span>
            {condition.notes ? (
              <span className="text-ink-3 block text-[12.5px]">{condition.notes}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

interface Draft {
  key: number;
  family: string;
  usual: string;
  max: string;
  purchase: string;
  net: boolean;
  shipping: string;
  notes: string;
}

function draftOf(condition: SupplierCondition, key: number): Draft {
  return {
    key,
    family: condition.family ?? '',
    usual: editableNumber(condition.usual_discount_pct),
    max: editableNumber(condition.max_discount_pct),
    purchase: editableNumber(condition.purchase_discount_pct),
    net: condition.purchase_net_in_tariff ?? false,
    shipping: editableNumber(condition.free_shipping_from),
    notes: condition.notes ?? '',
  };
}

function writeOf(draft: Draft): SupplierConditionWrite {
  const decimal = (value: string) => (value.trim() ? decimalFor(value) : null);
  return {
    family: draft.family.trim(),
    usual_discount_pct: decimal(draft.usual),
    max_discount_pct: decimal(draft.max),
    purchase_discount_pct: draft.net ? null : decimal(draft.purchase),
    purchase_net_in_tariff: draft.net,
    free_shipping_from: decimal(draft.shipping),
    notes: draft.notes.trim() || null,
  };
}

/**
 * Every condition at once, as the API takes them. Only for whoever manages the documents,
 * who sees every field: the form sends back what it was given, so a field the person could
 * not see would be saved empty. A row without family is the general one, and there is
 * always one.
 */
function ConditionsDialog({
  supplier,
  rows,
  onDone,
}: {
  supplier: Supplier;
  rows: SupplierCondition[];
  onDone: () => void;
}) {
  const id = useId();
  const put = usePutConditions(supplier.id);
  const families = useFamilies(supplier.id);
  const [next, setNext] = useState(rows.length + 1);
  const [drafts, setDrafts] = useState<Draft[]>(() => {
    const general = rows.find((row) => row.family === '');
    return [
      draftOf(general ?? { family: '' }, 0),
      ...rows.filter((row) => row.family !== '').map((row, index) => draftOf(row, index + 1)),
    ];
  });

  function change(key: number, patch: Partial<Draft>) {
    setDrafts((current) =>
      current.map((each) => (each.key === key ? { ...each, ...patch } : each)),
    );
  }

  function save() {
    const conditions = drafts
      .map(writeOf)
      .filter(
        (each) =>
          each.family === '' ||
          [
            each.usual_discount_pct,
            each.max_discount_pct,
            each.purchase_discount_pct,
            each.free_shipping_from,
            each.notes,
          ].some((value) => value !== null) ||
          each.purchase_net_in_tariff,
      );
    put.mutate(conditions, { onSuccess: onDone });
  }

  const blankFamily = drafts.some((each, index) => index > 0 && !each.family.trim());

  return (
    <Dialog
      title={`Condiciones de ${supplier.name}`}
      description="Los porcentajes, en tanto por cien. Lo que una familia deja en blanco lo toma de la fila general."
      size="lg"
      onClose={onDone}
      footer={
        <>
          <Button variant="secondary" onClick={onDone} disabled={put.isPending}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={put.isPending || blankFamily}>
            {put.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <datalist id={`${id}-families`}>
        {(families.data ?? []).map((each) => (
          <option key={each} value={each} />
        ))}
      </datalist>
      <div className="flex flex-col gap-3">
        {drafts.map((draft, index) => (
          <fieldset key={draft.key} className="border-line rounded-lg border p-3">
            <legend className="text-ink px-1 text-[13.5px] font-medium">
              {index === 0 ? 'General' : 'Familia'}
            </legend>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {index > 0 ? (
                <label className="col-span-2 flex flex-col gap-1 text-[13px] sm:col-span-3">
                  <span className="text-ink-2 font-medium">Familia o grupo de descuento</span>
                  <span className="flex gap-2">
                    <input
                      className="field w-full"
                      list={`${id}-families`}
                      value={draft.family}
                      maxLength={120}
                      onChange={(event) => change(draft.key, { family: event.target.value })}
                      placeholder="Como la escribe la tarifa"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<XIcon size={14} />}
                      onClick={() =>
                        setDrafts((current) => current.filter((each) => each.key !== draft.key))
                      }
                    >
                      Quitar
                    </Button>
                  </span>
                </label>
              ) : null}
              <NumberField
                label="Dto. habitual (%)"
                value={draft.usual}
                onChange={(value) => change(draft.key, { usual: value })}
              />
              <NumberField
                label="Dto. máximo (%)"
                value={draft.max}
                onChange={(value) => change(draft.key, { max: value })}
              />
              <NumberField
                label="Porte gratis desde (€)"
                value={draft.shipping}
                onChange={(value) => change(draft.key, { shipping: value })}
              />
              <NumberField
                label="Dto. de compra (%)"
                value={draft.purchase}
                disabled={draft.net}
                onChange={(value) => change(draft.key, { purchase: value })}
              />
              <label className="text-ink-2 col-span-2 flex items-center gap-2 self-end pb-2 text-[13.5px]">
                <input
                  type="checkbox"
                  className="accent-accent size-4"
                  checked={draft.net}
                  onChange={(event) => change(draft.key, { net: event.target.checked })}
                />
                Se compra al precio neto de la tarifa
              </label>
              <label className="col-span-2 flex flex-col gap-1 text-[13px] sm:col-span-3">
                <span className="text-ink-2 font-medium">Notas</span>
                <input
                  className="field w-full"
                  value={draft.notes}
                  maxLength={2000}
                  aria-describedby={`${id}-notes-${draft.key}`}
                  onChange={(event) => change(draft.key, { notes: event.target.value })}
                />
                <span id={`${id}-notes-${draft.key}`} className="text-ink-3 text-[12.5px]">
                  Las ve todo el equipo, también los comerciales.
                </span>
              </label>
            </div>
          </fieldset>
        ))}
        <div>
          <Button
            variant="secondary"
            size="sm"
            icon={<PlusIcon size={14} />}
            onClick={() => {
              setDrafts((current) => [...current, draftOf({ family: '' }, next)]);
              setNext((value) => value + 1);
            }}
          >
            Añadir una familia
          </Button>
        </div>
        {blankFamily ? (
          <p className="text-ink-3 text-[13px]">Pon el nombre de cada familia, o quítala.</p>
        ) : null}
        {put.isError ? (
          <ErrorMessage error={put.error}>No se pudieron guardar las condiciones</ErrorMessage>
        ) : null}
      </div>
    </Dialog>
  );
}

function NumberField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1 text-[13px]">
      <span className="text-ink-2 font-medium">{label}</span>
      <input
        className="field w-full tabular-nums"
        inputMode="decimal"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
